import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, TrendingUp, Target } from "lucide-react";
import {
  RadialBarChart, RadialBar, PolarAngleAxis,
  BarChart, Bar, LineChart, Line, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Quality, downloadAuthed } from "@/lib/api";
import { AlertTriangle, Flame, TrendingUp as TrendUp, TrendingDown, FileWarning, Sparkles, ClipboardSignature, RefreshCw } from "lucide-react";

/**
 * MonthlyView — KPI dashboard for a chosen month.
 *
 * Layout (top to bottom):
 *   • Month picker + Export XLSX / PDF buttons
 *   • KPI card row (admits, D/C, avg census, open/completed tasks, avg d-to-close)
 *   • LOS distribution bar chart (0-3, 4-7, 8-14, 15-30, 31+)
 *   • Compliance-vs-goal gauges (radial charts, one per indicator with target)
 *   • Full compliance table (indicator × target × actual × on-target)
 *   • Heatmap grid — indicators × ISO weeks, red→green cells
 *
 * All numbers come from /api/quality/monthly. Charts use recharts (already
 * pinned in package.json). Nothing here is patient-identifiable.
 */
function InsightList({ title, items, tone, testid }) {
  const bg = tone === "good" ? "bg-white border-[#c7e0d5]"
    : tone === "bad" ? "bg-white border-[#e0c4c1]"
    : "bg-white border-[#e2e8f0]";
  const chip = tone === "good" ? "bg-[#f0f9ff] text-[#0284c7]"
    : tone === "bad" ? "bg-[#fbebe7] text-[#b94a48]"
    : "bg-[#f1f5f9] text-[#6e5b1a]";
  return (
    <div className={`p-3 rounded-md border ${bg}`} data-testid={testid}>
      <div className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest mb-2 ${chip}`}>
        {title}
      </div>
      <ul className="space-y-1.5">
        {(items || []).length === 0 && <li className="text-xs text-[#475569] italic">(none)</li>}
        {(items || []).map((it, i) => (
          <li key={i} className="text-xs text-[#0f172a] leading-relaxed flex gap-1.5">
            <span className="text-[#475569]">•</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MonthlyView() {
  const initialMonth = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);
  const [month, setMonth] = useState(initialMonth);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [capOpenFor, setCapOpenFor] = useState(null); // compliance-row for which the CAP dialog is open
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [signer, setSigner] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await Quality.monthly(month);
        if (!cancelled) setReport(r);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load monthly report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [month]);

  // Load insights (cached; only calls Claude once per month).
  useEffect(() => {
    let cancelled = false;
    setInsights(null);
    (async () => {
      setInsightsLoading(true);
      try {
        const r = await Quality.insights(month, false);
        if (!cancelled) setInsights(r);
      } catch (e) {
        // Non-fatal — the board PDF still exports with a fallback narrative.
        if (!cancelled) setInsights(null);
      } finally {
        if (!cancelled) setInsightsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [month]);

  const refreshInsights = async () => {
    setInsightsLoading(true);
    try {
      const r = await Quality.insights(month, true);
      setInsights(r);
      toast.success("Insights refreshed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to refresh insights");
    } finally {
      setInsightsLoading(false);
    }
  };

  if (loading && !report) {
    return <div className="p-8 text-center text-sm text-[#475569]">Loading monthly report…</div>;
  }
  if (!report) return null;

  const kpis = report.kpis;
  const withTargets = report.compliance.filter((c) => c.target_pct > 0);
  const belowTarget = report.compliance.filter((c) => c.cap_required);
  const capsMissing = belowTarget.filter((c) => !c.cap).length;
  const losData = Object.entries(kpis.los_distribution).map(([k, v]) => ({ label: k, value: v }));

  const reload = async () => {
    const r = await Quality.monthly(month);
    setReport(r);
  };

  return (
    <div className="px-6 py-4 space-y-4 overflow-auto" data-testid="quality-monthly-view">
      {/* Month + export */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] font-semibold tracking-widest uppercase text-[#475569] mb-1">
            Month
          </label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border border-[#e2e8f0] rounded px-2 py-1.5 text-sm bg-white"
            data-testid="monthly-picker"
          />
        </div>
        <div className="ml-auto flex flex-wrap items-end gap-2">
          <div className="flex flex-col">
            <label className="block text-[10px] font-semibold tracking-widest uppercase text-[#475569] mb-1">
              Board signer (optional)
            </label>
            <input
              value={signer}
              onChange={(e) => setSigner(e.target.value)}
              placeholder="e.g. Dr. J. Smith, MD"
              className="border border-[#e2e8f0] rounded px-2 py-1.5 text-sm bg-white w-56"
              data-testid="board-signer-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => downloadAuthed(Quality.monthlyXlsxUrl(month), `quality-monthly-${month}.xlsx`)}
            className="border-[#e2e8f0]"
            data-testid="monthly-export-xlsx"
          >
            <Download className="w-4 h-4 mr-1.5" /> Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadAuthed(Quality.monthlyPdfUrl(month), `quality-monthly-${month}.pdf`)}
            className="border-[#e2e8f0]"
            data-testid="monthly-export-pdf"
          >
            <FileText className="w-4 h-4 mr-1.5" /> PDF
          </Button>
          <Button
            onClick={() => downloadAuthed(Quality.boardOnePagerUrl(month, signer), `board-one-pager-${month}.pdf`)}
            className="bg-[#0284c7] hover:bg-[#0369a1] text-white"
            data-testid="board-one-pager-btn"
          >
            <ClipboardSignature className="w-4 h-4 mr-1.5" /> Board one-pager
          </Button>
        </div>
      </div>

      {/* AI Auto-Insights card */}
      <div
        className="rounded-xl border border-[#e2e8f0] border-t-2 border-t-[#38bdf8] bg-white shadow-sm p-6 space-y-4"
        data-testid="insights-card"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#0284c7]" />
          <div className="font-heading text-lg font-bold text-[#0f172a] tracking-tight">
            AI Auto-Insights — {month}
          </div>
          {insights?.model && (
            <span className="ml-2 text-[10px] uppercase tracking-widest text-[#94a3b8] font-medium">
              {insights.model === "fallback" ? "Deterministic summary" : insights.model}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={refreshInsights}
            disabled={insightsLoading}
            data-testid="insights-refresh"
            className="ml-auto text-[#0284c7] hover:text-[#0369a1] hover:bg-[#f0f9ff]"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${insightsLoading ? "animate-spin" : ""}`} />
            {insightsLoading ? "Thinking…" : "Refresh"}
          </Button>
        </div>
        {!insights && insightsLoading && (
          <div className="text-sm text-[#475569] italic">Generating narrative from Claude Sonnet…</div>
        )}
        {!insights && !insightsLoading && (
          <div className="text-sm text-[#475569] italic">
            Insights unavailable — click <em>Refresh</em> to try again.
          </div>
        )}
        {insights && (
          <>
            <p className="text-sm leading-relaxed text-[#0f172a] whitespace-pre-line" data-testid="insights-narrative">
              {insights.narrative}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 border-t border-[#f1f5f9]">
              <InsightList title="Highlights" items={insights.highlights} tone="good" testid="insights-highlights" />
              <InsightList title="Regressions" items={insights.regressions} tone="bad" testid="insights-regressions" />
              <InsightList title="Recommendations" items={insights.recommendations} tone="neutral" testid="insights-recommendations" />
            </div>
          </>
        )}
      </div>

      {/* SLA overdue tasks — big red banner so nothing rots in the backlog. */}
      {report.overdue_tasks && report.overdue_tasks.length > 0 && (
        <div className="rounded-lg border-2 border-[#b94a48] bg-[#fbebe7] px-4 py-3" data-testid="sla-alert-banner">
          <div className="flex items-center gap-2 mb-1.5">
            <AlertTriangle className="w-5 h-5 text-[#b94a48]" />
            <span className="font-heading text-sm font-bold text-[#b94a48]">
              {report.overdue_tasks.length} overdue task{report.overdue_tasks.length > 1 ? "s" : ""}
            </span>
            <span className="text-xs text-[#8a3c3c]">— past due date and still open</span>
          </div>
          <ul className="space-y-1">
            {report.overdue_tasks.slice(0, 6).map((t) => (
              <li key={t.id} className="text-xs text-[#8a3c3c] flex items-center gap-2" data-testid={`sla-overdue-${t.id}`}>
                <span className="px-1.5 py-0.5 rounded bg-white/70 text-[#b94a48] font-mono font-bold">
                  +{t.days_overdue}d
                </span>
                <span className="font-semibold">{t.title}</span>
                {t.patient_name && <span>· {t.patient_name}</span>}
                <span className="italic">· assigned to {t.director_name}</span>
                {t.created_by && <span>· from {t.created_by}</span>}
              </li>
            ))}
            {report.overdue_tasks.length > 6 && (
              <li className="text-xs italic text-[#8a3c3c]">+ {report.overdue_tasks.length - 6} more…</li>
            )}
          </ul>
        </div>
      )}

      {/* CAP-missing banner — anything below target without a filed Corrective Action Plan */}
      {capsMissing > 0 && (
        <div className="rounded-lg border-2 border-[#8a6b1a] bg-[#fff6e5] px-4 py-3" data-testid="cap-alert-banner">
          <div className="flex items-center gap-2 mb-1.5">
            <FileWarning className="w-5 h-5 text-[#8a6b1a]" />
            <span className="font-heading text-sm font-bold text-[#8a6b1a]">
              {capsMissing} indicator{capsMissing > 1 ? "s" : ""} need{capsMissing > 1 ? "" : "s"} a Corrective Action Plan
            </span>
            <span className="text-xs text-[#6e5b1a]">— required for the QAPI meeting</span>
          </div>
          <ul className="space-y-1">
            {belowTarget.filter((r) => !r.cap).slice(0, 5).map((r) => (
              <li key={r.indicator_id} className="text-xs text-[#6e5b1a] flex items-center gap-2">
                <span className="font-semibold">{r.label}</span>
                <span>· target {r.target_pct}% · actual {r.actual_pct}%</span>
                <button
                  onClick={() => setCapOpenFor(r)}
                  className="ml-auto px-2 py-0.5 rounded bg-[#8a6b1a] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#6e5b1a]"
                  data-testid={`cap-file-${r.indicator_id}`}
                >
                  File CAP
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="monthly-kpis">
        {[
          { k: "total_admits", label: "Admits" },
          { k: "total_discharges", label: "Discharges" },
          { k: "avg_daily_census", label: "Avg census" },
          { k: "total_patients", label: "Patients" },
          { k: "task_open", label: "Open tasks", accent: "#b94a48" },
          { k: "task_completed", label: "Completed", accent: "#0284c7" },
          { k: "avg_task_close_days", label: "Avg d/close" },
        ].map((c) => (
          <div
            key={c.k}
            className="rounded-lg border border-[#e2e8f0] bg-white p-3"
            data-testid={`kpi-card-${c.k}`}
          >
            <div className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">{c.label}</div>
            <div
              className="mt-1 font-heading text-2xl font-bold"
              style={{ color: c.accent || "#0f172a" }}
            >
              {kpis[c.k]}
            </div>
          </div>
        ))}
      </div>

      {/* LOS distribution + Gauges */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 rounded-lg border border-[#e2e8f0] bg-white p-4">
          <h3 className="font-heading text-sm font-semibold text-[#0f172a] mb-2">
            LOS distribution
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={losData} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} />
              <YAxis tick={{ fontSize: 11, fill: "#475569" }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="value" fill="#0284c7" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-2 rounded-lg border border-[#e2e8f0] bg-white p-4">
          <h3 className="font-heading text-sm font-semibold text-[#0f172a] mb-2 flex items-center gap-2">
            <Target className="w-4 h-4 text-[#d96c4e]" /> Compliance vs goal
          </h3>
          {withTargets.length === 0 ? (
            <p className="text-xs text-[#475569] italic py-4">
              No indicators have a target set. Open <em>Manage directors &amp; indicators</em> and set a target % on any indicator to see a gauge here.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {withTargets.slice(0, 8).map((c) => (
                <ComplianceGauge key={c.indicator_id} row={c} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Compliance table */}
      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <div className="px-4 py-2 border-b border-[#e2e8f0] bg-[#faf9f5] font-semibold text-sm text-[#0f172a] flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#0284c7]" /> Compliance detail
        </div>
        <div className="overflow-auto max-h-[300px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-left border-b border-[#e2e8f0]">
                <th className="px-3 py-2 font-medium text-[#475569]">Director</th>
                <th className="px-3 py-2 font-medium text-[#475569]">Indicator</th>
                <th className="px-3 py-2 font-medium text-[#475569] text-center">Target</th>
                <th className="px-3 py-2 font-medium text-[#475569] text-center">Actual</th>
                <th className="px-3 py-2 font-medium text-[#475569] text-center">Δ prev mo</th>
                <th className="px-3 py-2 font-medium text-[#475569] text-center">6-mo trend</th>
                <th className="px-3 py-2 font-medium text-[#475569] text-center">Streak</th>
                <th className="px-3 py-2 font-medium text-[#475569] text-center">Status</th>
                <th className="px-3 py-2 font-medium text-[#475569] text-center">CAP</th>
              </tr>
            </thead>
            <tbody>
              {report.compliance.map((row) => {
                let rowBg = "";
                if (row.target_pct > 0) {
                  const ratio = row.actual_pct / row.target_pct;
                  if (ratio >= 1) rowBg = "bg-[#f0f9ff]";
                  else if (ratio >= 0.8) rowBg = "bg-[#fff6e5]";
                  else rowBg = "bg-[#fbebe7]";
                }
                const delta = row.delta_pct;
                const streak = row.streak_months || 0;
                const trendData = (row.history || []).map((h) => ({ pct: h.pct ?? 0, hasData: h.pct !== null }));
                return (
                <tr key={row.indicator_id} className={`border-b border-[#f0efe9] ${rowBg}`} data-testid={`compliance-row-${row.indicator_id}`}>
                  <td className="px-3 py-2">
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-2"
                      style={{ background: row.director_color }}
                    />
                    {row.director_name}
                  </td>
                  <td className="px-3 py-2 text-[#0f172a]">{row.label}</td>
                  <td className="px-3 py-2 text-center font-mono text-xs">
                    {row.target_pct ? `${row.target_pct}%` : "—"}
                  </td>
                  <td className="px-3 py-2 text-center font-mono text-xs font-semibold">
                    {row.actual_pct}%
                  </td>
                  <td className="px-3 py-2 text-center font-mono text-xs">
                    {delta === null || delta === undefined ? (
                      <span className="text-[#c4c4c0]">—</span>
                    ) : (
                      <span
                        className={
                          delta > 0
                            ? "text-[#0284c7] font-semibold"
                            : delta < 0
                            ? "text-[#b94a48] font-semibold"
                            : "text-[#475569]"
                        }
                        data-testid={`compliance-delta-${row.indicator_id}`}
                      >
                        {delta > 0 && <TrendUp className="inline w-3 h-3 mr-0.5" />}
                        {delta < 0 && <TrendingDown className="inline w-3 h-3 mr-0.5" />}
                        {delta > 0 ? "+" : ""}{delta}%
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-center" style={{ minWidth: 100 }}>
                    <div className="w-24 h-8 mx-auto" data-testid={`compliance-trend-${row.indicator_id}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trendData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                          <Line
                            type="monotone"
                            dataKey="pct"
                            stroke={row.on_target ? "#0284c7" : "#b94a48"}
                            strokeWidth={2}
                            dot={false}
                          />
                          <YAxis hide domain={[0, 100]} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {streak >= 2 && row.target_pct > 0 ? (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#fff6e5] text-[#8a6b1a] text-[10px] font-bold tracking-wider uppercase"
                        title={`On target ${streak} consecutive months`}
                        data-testid={`compliance-streak-${row.indicator_id}`}
                      >
                        <Flame className="w-3 h-3" /> {streak}mo
                      </span>
                    ) : (
                      <span className="text-[#c4c4c0]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.target_pct > 0 && (
                      <span
                        className={[
                          "px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase",
                          row.on_target
                            ? "bg-[#0284c7] text-white"
                            : row.actual_pct >= 0.8 * row.target_pct
                            ? "bg-[#8a6b1a] text-white"
                            : "bg-[#b94a48] text-white",
                        ].join(" ")}
                      >
                        {row.on_target
                          ? "✓ On target"
                          : row.actual_pct >= 0.8 * row.target_pct
                          ? "⚠ Watch"
                          : "✗ Below"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.cap_required ? (
                      row.cap ? (
                        <button
                          onClick={() => setCapOpenFor(row)}
                          className={[
                            "px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase",
                            row.cap.status === "completed"
                              ? "bg-[#f0f9ff] text-[#0284c7]"
                              : row.cap.status === "submitted"
                              ? "bg-[#fff6e5] text-[#8a6b1a]"
                              : "bg-[#f1f5f9] text-[#3f4643]",
                          ].join(" ")}
                          data-testid={`cap-open-${row.indicator_id}`}
                          title="Open Corrective Action Plan"
                        >
                          {row.cap.status}
                        </button>
                      ) : (
                        <button
                          onClick={() => setCapOpenFor(row)}
                          className="px-1.5 py-0.5 rounded bg-[#b94a48] text-white text-[10px] font-bold tracking-wider uppercase hover:bg-[#8a3c3c]"
                          data-testid={`cap-file-btn-${row.indicator_id}`}
                        >
                          ❗ File CAP
                        </button>
                      )
                    ) : (
                      <span className="text-[#c4c4c0]">—</span>
                    )}
                  </td>
                </tr>
              );})}
              {report.compliance.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-sm text-[#475569] italic">
                    No indicators — add some via <em>Manage directors &amp; indicators</em>.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Weekly compliance heatmap */}
      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <div className="px-4 py-2 border-b border-[#e2e8f0] bg-[#faf9f5] font-semibold text-sm text-[#0f172a]">
          Weekly compliance heatmap
        </div>
        <div className="overflow-auto p-3">
          <table className="text-xs border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="text-left px-2 py-1.5 font-medium text-[#475569] sticky left-0 bg-white" style={{ minWidth: 220 }}>Indicator</th>
                {report.heatmap.weeks.map((wk) => (
                  <th key={wk} className="px-2 py-1.5 font-medium text-[#475569] text-center" style={{ minWidth: 90 }}>
                    {wk}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.heatmap.rows.map((r) => (
                <tr key={r.indicator_id}>
                  <td className="px-2 py-1 border-t border-[#f0efe9] sticky left-0 bg-white text-[#0f172a]">
                    {r.label}
                  </td>
                  {r.cells.map((c, i) => (
                    <td
                      key={i}
                      className="px-2 py-1 border-t border-[#f0efe9] text-center font-mono"
                      style={{ background: heatColor(c.pct), color: heatText(c.pct) }}
                      title={c.total ? `${c.done}/${c.total}` : "no patients"}
                    >
                      {c.pct === null ? "—" : `${Math.round(c.pct)}%`}
                    </td>
                  ))}
                </tr>
              ))}
              {report.heatmap.rows.length === 0 && (
                <tr><td colSpan={report.heatmap.weeks.length + 1} className="px-3 py-6 text-center text-sm text-[#475569] italic">
                  No indicators yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {capOpenFor && (
        <CAPDialog
          row={capOpenFor}
          month={month}
          directors={report.directors}
          onClose={() => setCapOpenFor(null)}
          onSaved={async () => { setCapOpenFor(null); await reload(); }}
        />
      )}
    </div>
  );
}

function CAPDialog({ row, month, directors, onClose, onSaved }) {
  const initial = row.cap || {
    indicator_id: row.indicator_id,
    month,
    root_cause: "",
    action_steps: "",
    owner_director_id: row.director_id,
    due_date: "",
    follow_up_date: "",
    status: "draft",
    created_by: "",
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await Quality.saveCap({
        indicator_id: row.indicator_id,
        month,
        root_cause: form.root_cause,
        action_steps: form.action_steps,
        owner_director_id: form.owner_director_id,
        due_date: form.due_date,
        follow_up_date: form.follow_up_date,
        status: form.status,
        created_by: form.created_by,
      });
      onSaved && (await onSaved());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save CAP");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="cap-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Corrective Action Plan</DialogTitle>
          <DialogDescription className="text-[#475569]">
            <span className="font-semibold text-[#0f172a]">{row.label}</span> — target {row.target_pct}%, actual {row.actual_pct}%.
            Required for the QAPI meeting for the month of {month}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Root cause</Label>
            <textarea
              value={form.root_cause}
              onChange={(e) => setForm({ ...form, root_cause: e.target.value })}
              placeholder="Why did compliance fall below target this month?"
              className="mt-1 w-full min-h-[70px] border border-[#e2e8f0] rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#0284c7]"
              data-testid="cap-root-cause"
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Action steps</Label>
            <textarea
              value={form.action_steps}
              onChange={(e) => setForm({ ...form, action_steps: e.target.value })}
              placeholder="Numbered corrective actions with owners and dates"
              className="mt-1 w-full min-h-[100px] border border-[#e2e8f0] rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#0284c7]"
              data-testid="cap-action-steps"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Owner</Label>
              <select
                value={form.owner_director_id || ""}
                onChange={(e) => setForm({ ...form, owner_director_id: e.target.value })}
                className="mt-1 w-full h-9 border border-[#e2e8f0] rounded px-2 text-sm bg-white"
                data-testid="cap-owner"
              >
                <option value="">— select —</option>
                {directors.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Due date</Label>
              <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 border-[#e2e8f0]" data-testid="cap-due" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Follow-up</Label>
              <Input type="date" value={form.follow_up_date || ""} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} className="mt-1 border-[#e2e8f0]" data-testid="cap-followup" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Status</Label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="mt-1 w-full h-9 border border-[#e2e8f0] rounded px-2 text-sm bg-white"
                data-testid="cap-status"
              >
                <option value="draft">Draft</option>
                <option value="submitted">Submitted for QAPI review</option>
                <option value="completed">Completed / closed</option>
              </select>
            </div>
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Created by (initials)</Label>
              <Input value={form.created_by || ""} onChange={(e) => setForm({ ...form, created_by: e.target.value.toUpperCase() })} maxLength={4} className="mt-1 border-[#e2e8f0] font-mono uppercase" data-testid="cap-created-by" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={busy}
            className="bg-[#0284c7] hover:bg-[#0369a1] text-white"
            data-testid="cap-save"
          >
            {row.cap ? "Update CAP" : "File CAP"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ComplianceGauge({ row }) {
  const value = Math.min(100, Math.max(0, row.actual_pct));
  const target = row.target_pct;
  const good = row.on_target;
  const data = [{ name: row.label, value, fill: good ? "#0284c7" : "#b94a48" }];
  return (
    <div className="rounded-md border border-[#efece2] p-2" data-testid={`gauge-${row.indicator_id}`}>
      <div className="text-[10px] font-semibold text-[#475569] truncate" title={row.label}>
        {row.label}
      </div>
      <div className="relative">
        <ResponsiveContainer width="100%" height={90}>
          <RadialBarChart
            innerRadius="70%"
            outerRadius="100%"
            data={data}
            startAngle={220}
            endAngle={-40}
            barSize={10}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar background dataKey="value" cornerRadius={6} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="text-lg font-bold font-heading" style={{ color: good ? "#0284c7" : "#b94a48" }}>
            {value}%
          </div>
          <div className="text-[9px] text-[#475569]">target {target}%</div>
        </div>
      </div>
    </div>
  );
}

// Ramp colors matching the XLSX heatmap
function heatColor(pct) {
  if (pct === null) return "#f7f4ec";
  if (pct >= 90) return "#cfe9de";
  if (pct >= 70) return "#e5f1e8";
  if (pct >= 50) return "#fff6e5";
  if (pct >= 30) return "#fde4d8";
  return "#fbebe7";
}
function heatText(pct) {
  if (pct === null) return "#475569";
  return "#0f172a";
}
